// NIGHT-DIRECTSTAFF-2026-10-23b §1: offers on a candidate -- the job (a facility request or a facility typed in), the pay
// package lines as staff enter them (hourly taxable, weekly stipend, other; NEVER computed from the pay index or anything
// else), and what became of it: offered -> accepted | declined (a reason) | expired. Accepting creates or updates the
// placement (Confirmed: agreed, not started -- staff set Started on the placement). The database does every write
// (migrations/2026-10-23-offers.sql: offer_create, offer_set_status) and writes one timeline line per step. Dark until the
// hub applies it (the table missing -> { ready:false }).

import { displayLine } from './draftCheck';

export const OFFER_LOG_TYPE = 'offer';
export const OFFER_COLS = 'id,candidate_id,facility_request_id,facility,discipline,setting,city,state,start_date,length_weeks,pay_hourly_taxable,pay_weekly_stipend,pay_other,status,status_at,status_by,decline_reason,placement_id,notes,created_at,created_by';
export const OFFER_STATUSES = { offered: 'Offered', accepted: 'Accepted', declined: 'Declined', expired: 'Expired' };
export const DISCIPLINES = ['PT', 'PTA', 'OT', 'COTA', 'SLP', 'SLPA'];
export const SETTINGS = ['Hospital', 'SNF', 'Outpatient', 'Home Health', 'School', 'Other'];
export const LIMITS = { facility: 200, city: 80, pay_other: 200, notes: 2000, decline_reason: 300 };
export const DARK_WHY = 'Offers turn on when the hub applies 2026-10-23-offers.sql.';
export const DECLINE_REASONS = ['Took another offer', 'Pay too low', 'Location', 'Start date', 'Shift or schedule', 'Not traveling now'];

const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const missing = (e) => !!e && (/PGRST205|42P01/.test(String(e.code || '')) || /could not find the table|does not exist/i.test(String(e.message || '')));
const money = (v) => { const n = Number(v); return Number.isFinite(n) ? n.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 }) : ''; };
const day = (d) => { const x = s(d); if (!/^\d{4}-\d{2}-\d{2}/.test(x)) return ''; const t = new Date(`${x.slice(0, 10)}T12:00:00Z`);
  return t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }); };

/** "$24.50/hr taxable · $1,500/wk stipend · Travel $500" -- the lines exactly as entered; '' when none */
export function payLine(o) {
  if (!o) return '';
  return [s(o.pay_hourly_taxable) !== '' ? `$${money(o.pay_hourly_taxable)}/hr taxable` : '', s(o.pay_weekly_stipend) !== '' ? `$${money(o.pay_weekly_stipend)}/wk stipend` : '', s(o.pay_other)]
    .filter(Boolean).join(' · ');
}
/** "PT · Hospital · Denver, CO · Synthetic General · starts Nov 9, 2026 · 13 weeks" */
export function jobLine(o) {
  if (!o) return '';
  return [s(o.discipline), s(o.setting), [s(o.city), s(o.state)].filter(Boolean).join(', '),
    s(o.facility) || (o.facility_request_id ? `Facility request ${o.facility_request_id}` : ''), o.start_date ? `starts ${day(o.start_date)}` : '',
    o.length_weeks ? `${o.length_weeks} weeks` : ''].filter(Boolean).join(' · ');
}

const NUM = /^\d+(\.\d{1,2})?$/;
/** form -> { errors: { field: words }, payload } ; the payload is what offer_create takes */
export function validateOffer(f = {}) {
  const errors = {};
  const reqId = s(f.facility_request_id);
  if (!reqId && !s(f.facility)) errors.facility = 'Choose a facility request or type the facility.';
  if (s(f.facility).length > LIMITS.facility) errors.facility = `Up to ${LIMITS.facility} characters.`;
  if (!DISCIPLINES.includes(s(f.discipline).toUpperCase())) errors.discipline = 'Choose the discipline.';
  if (s(f.state) && !/^[A-Za-z]{2}$/.test(s(f.state))) errors.state = 'Two letters (CO).';
  if (s(f.city).length > LIMITS.city) errors.city = `Up to ${LIMITS.city} characters.`;
  if (s(f.start_date) && !/^\d{4}-\d{2}-\d{2}$/.test(s(f.start_date))) errors.start_date = 'A date.';
  if (s(f.length_weeks) && !(/^\d+$/.test(s(f.length_weeks)) && Number(f.length_weeks) >= 1 && Number(f.length_weeks) <= 104)) errors.length_weeks = 'Whole weeks, 1 to 104.';
  for (const k of ['pay_hourly_taxable', 'pay_weekly_stipend']) {
    const v = s(f[k]).replace(/^\$/, '').replace(/,/g, '');
    if (v && (!NUM.test(v) || Number(v) > 999999.99)) errors[k] = 'Dollars, like 24.50.';
  }
  if (s(f.pay_other).length > LIMITS.pay_other) errors.pay_other = `Up to ${LIMITS.pay_other} characters.`;
  if (s(f.notes).length > LIMITS.notes) errors.notes = `Up to ${LIMITS.notes} characters.`;
  const payload = {
    facility_request_id: reqId || null, facility: s(f.facility) || null, discipline: s(f.discipline).toUpperCase(), setting: s(f.setting) || null,
    city: s(f.city) || null, state: s(f.state).toUpperCase() || null, start_date: s(f.start_date) || null, length_weeks: s(f.length_weeks) || null,
    pay_hourly_taxable: s(f.pay_hourly_taxable).replace(/^\$/, '').replace(/,/g, '') || null, pay_weekly_stipend: s(f.pay_weekly_stipend).replace(/^\$/, '').replace(/,/g, '') || null,
    pay_other: s(f.pay_other) || null, notes: s(f.notes) || null,
  };
  return { errors, payload };
}
/** A facility request -> the form's fields it fills (the job; never the facility's bill rate) */
export function fromRequest(r) {
  if (!r) return {};
  return { facility_request_id: String(r.id), discipline: s(r.discipline), setting: s(r.setting), city: s(r.city), state: s(r.state),
    start_date: s(r.start_date).slice(0, 10), length_weeks: r.length_weeks ? String(r.length_weeks) : '' };
}
/** "Request 12 · PT · Denver, CO · Nov 9, 2026" for the picker */
export const requestLabel = (r) => [`Request ${r.id}`, s(r.discipline), [s(r.city), s(r.state)].filter(Boolean).join(', '), r.start_date ? day(r.start_date) : ''].filter(Boolean).join(' · ');

// NIGHT-DIRECTSTAFF-2026-10-23c §3: the offer summary staff send to the candidate (the Send box: text or email; never automatic).
// The job (a facility request's number is staff-side, so it is left out), the pay lines exactly as staff typed them, a
// "reply YES to accept or call us" line and the business line (from the facts file -- never typed). One per offer per day (ET).
export function summaryJobLine(o) {
  return jobLine({ ...o, facility_request_id: null });
}
export function offerSummary(o, name, line = displayLine()) {
  const first = String(name || '').trim().split(/\s+/)[0] || '';
  const hi = /^[A-Za-z][A-Za-z'.-]*$/.test(first) ? `Hi ${first},` : 'Hi,';
  const pay = payLine(o);
  return `${hi} here's the offer we talked about: ${summaryJobLine(o)}.${pay ? ` Pay: ${pay}.` : ''} Reply YES to accept, or call us at ${line}.`;
}
// By email the reply comes back by email: "Reply YES" is for a text only (agents 10-23c: a YES texted back on a thread staff never
// texted goes to the agent, and could confirm a pending job-alert sign-up).
export const forEmail = (body) => String(body || '').replace(/Reply YES to accept, or call us/, 'Reply to this email to accept, or call us');
export const summaryNote = (offerId, channel, by) => `Offer ${offerId} summary sent by ${channel === 'copy' ? 'copied text' : channel}${by ? ` by ${by}` : ''}`;
const etDay = (d) => new Date(d).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
/** activity rows -> Set of offer ids whose summary went out today (ET) */
export function summarizedToday(rows, now = new Date()) {
  const today = etDay(now); const out = new Set();
  for (const r of rows || []) {
    const m = /^Offer (\d+) summary sent by /.exec(String(r.note || ''));
    if (m && r.created_at && etDay(r.created_at) === today) out.add(Number(m[1]));
  }
  return out;
}

export const OFFER_ERRORS = {
  'offer:reason_required': 'Say why they declined (up to 300 characters).',
  'offer:not_found': 'That offer is no longer there. Refresh.',
  'offer:no_candidate': 'That candidate is no longer there.',
  'offer:bad_status': 'Choose Accepted, Declined or Expired.',
  'offer:already_accepted': 'Already accepted.', 'offer:already_declined': 'Already declined.', 'offer:already_expired': 'Already expired.',
  offers_where_check: 'Choose a facility request or type the facility.',
  offers_values_check: 'Check the discipline, state, weeks and pay amounts.',
};
const plain = (e) => { const m = String((e && e.message) || ''); const k = Object.keys(OFFER_ERRORS).find((x) => m.includes(x)); return k ? OFFER_ERRORS[k] : m || 'Not saved'; };

/** Reads (RLS: staff) and the two writes (the functions), over the signed-in staff client. */
export function makeOffersApi(supabase) {
  return {
    /** -> { ready:false } before the migration; { ready:true, rows } (newest first) */
    async load(candidateId) {
      const { data, error } = await supabase.from('offers').select(OFFER_COLS).eq('candidate_id', candidateId).order('created_at', { ascending: false }).limit(200);
      if (error) { if (missing(error)) return { ready: false }; throw new Error(error.message); }
      return { ready: true, rows: data || [] };
    },
    /** DAY-DIRECTSTAFF-2026-11-10 §1: every offer still Offered, for Today -> rows | null (the table not there, or unreadable) */
    async openOffers() {
      try { const { data, error } = await supabase.from('offers').select(OFFER_COLS).eq('status', 'offered').order('created_at', { ascending: true }).limit(500); return error ? null : (data || []); }
      catch (e) { return null; }
    },
    /** open facility requests to offer against ([] when the portal tables are not there) */
    async requests() {
      const { data, error } = await supabase.from('facility_requests').select('id,discipline,setting,city,state,start_date,length_weeks,status')
        .order('created_at', { ascending: false }).limit(200);
      if (error) return [];
      return (data || []).filter((r) => !['filled', 'closed', 'cancelled'].includes(s(r.status)));
    },
    async create(candidateId, form) {
      const { errors, payload } = validateOffer(form);
      if (Object.keys(errors).length) { const e = new Error(Object.values(errors)[0]); e.errors = errors; throw e; }
      const { data, error } = await supabase.rpc('offer_create', { p_candidate_id: candidateId, p: payload });
      if (error) throw new Error(plain(error));
      return data;
    },
    /** §3: the offer-summary lines on this candidate's timeline (for "one per offer per day") */
    async summaries(candidateId) {
      const { data, error } = await supabase.from('activity_log').select('note,created_at').eq('candidate_id', candidateId).eq('type', OFFER_LOG_TYPE)
        .like('note', 'Offer % summary sent by %').order('created_at', { ascending: false }).limit(200);
      return error ? [] : (data || []);
    },
    async logSummary(candidateId, offerId, channel, by) {
      const { error } = await supabase.from('activity_log').insert({ candidate_id: candidateId, date: new Date().toISOString().slice(0, 10), type: OFFER_LOG_TYPE,
        note: summaryNote(offerId, channel, by) });
      if (error) throw new Error(`Sent, but not logged on the timeline: ${error.message}`);
    },
    async setStatus(id, status, reason = null) {
      const { data, error } = await supabase.rpc('offer_set_status', { p_offer_id: id, p_status: status, p_reason: reason });
      if (error) throw new Error(plain(error));
      return data;
    },
  };
}

/** The timeline line the database writes -> { title, line, tone } | null */
export function parseOfferNote(note) {
  const sm = /^Offer (\d+) summary sent by (text|email|copied text)(?: by (.+))?$/.exec(s(note));
  if (sm) return { id: Number(sm[1]), title: 'Offer summary sent', line: `By ${sm[2]}${sm[3] ? ` · by ${sm[3]}` : ''}`, tone: undefined };
  const m = /^Offer (\d+) (made|accepted|declined|expired) by ([^:·]+?)(?:: (.*?))?(?: · placement ([0-9a-f-]{36}))?$/.exec(s(note));
  if (!m) return null;
  const title = { made: 'Offer made', accepted: 'Offer accepted', declined: 'Offer declined', expired: 'Offer expired' }[m[2]];
  const line = m[2] === 'made' ? `${m[4] || ''} · by ${m[3]}` : m[2] === 'declined' ? `${m[4] || ''} · by ${m[3]}` : m[2] === 'accepted' ? `Placement confirmed · by ${m[3]}` : `by ${m[3]}`;
  return { id: Number(m[1]), title, line: line.replace(/^ · /, ''), tone: m[2] === 'accepted' ? 'good' : m[2] === 'declined' ? 'bad' : undefined };
}
